import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Building2, Upload, FileText, Check, X, Eye, Trash2, 
  ToggleLeft, ToggleRight, Save, RefreshCw, ChevronDown, 
  ChevronRight, AlertCircle, CheckCircle2, MapPin, Settings, Loader2
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

const PlantillasAseguradorasPage = () => {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [editValoresActivos, setEditValoresActivos] = useState({});
  const [editCamposActivos, setEditCamposActivos] = useState({});
  const [editValoresMapeados, setEditValoresMapeados] = useState({});
  const [editActiveTab, setEditActiveTab] = useState('valores');
  const [editNombreAseg, setEditNombreAseg] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editExpandedCats, setEditExpandedCats] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [camposActivos, setCamposActivos] = useState({});
  const [activeResultTab, setActiveResultTab] = useState('todos_valores');
  const [camposSistema, setCamposSistema] = useState([]);
  const [valoresEditados, setValoresEditados] = useState({});
  const [valoresActivos, setValoresActivos] = useState({});
  const [nombreAseguradora, setNombreAseguradora] = useState("");

  const fetchPlantillas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/plantillas-aseguradoras/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlantillas(data.plantillas || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error al cargar las plantillas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlantillas();
    // Cargar campos del sistema para los dropdowns
    const fetchCampos = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/plantillas-aseguradoras/campos-disponibles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCamposSistema(data.campos || []);
        }
      } catch (e) { console.error('Error loading campos:', e); }
    };
    fetchCampos();
  }, [fetchPlantillas]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setAnalysisResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1
  });

  const analyzeDocument = async () => {
    if (!selectedFile) return;

    setAnalyzing(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/plantillas-aseguradoras/analizar-documento`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysisResult(data);
        
        // Inicializar campos activos
        const activos = {};
        data.campos_encontrados?.forEach(campo => {
          activos[campo.id] = true;
        });
        setCamposActivos(activos);
        
        // Inicializar mapeos y estados de valores leídos
        const editados = {};
        const activosValores = {};
        data.todos_los_valores?.forEach((valor, idx) => {
          editados[idx] = valor.campo_sistema_sugerido || 'sin_mapear';
          activosValores[idx] = true;
        });
        setValoresEditados(editados);
        setValoresActivos(activosValores);
        
        // Expandir todas las categorías
        const categorias = {};
        data.campos_encontrados?.forEach(campo => {
          const cat = getCategoriaDelCampo(campo.id);
          categorias[cat] = true;
        });
        setExpandedCategories(categorias);
        
        // Pre-llenar nombre de aseguradora si se detectó
        setNombreAseguradora(data.nombre_aseguradora || "");
        
        toast.success(`Documento analizado: ${data.total_campos_detectados} campos encontrados`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al analizar el documento');
      }
    } catch (error) {
      console.error('Error analyzing document:', error);
      toast.error('Error al analizar el documento');
    } finally {
      setAnalyzing(false);
    }
  };

  const getCategoriaDelCampo = (campoId) => {
    const categorias = {
      'aseguradora': ['nombre_aseguradora', 'rnc_aseguradora', 'direccion_aseguradora', 'telefono_aseguradora'],
      'asegurado': ['nombre_asegurado', 'rnc_cedula_asegurado', 'direccion_asegurado', 'telefono_asegurado', 'correo_asegurado'],
      'poliza': ['numero_poliza', 'tipo_poliza', 'ramo', 'producto_plan', 'fecha_emision', 'vigencia_desde', 'vigencia_hasta', 'forma_pago', 'moneda'],
      'vehiculo': ['marca_vehiculo', 'modelo_vehiculo', 'año_vehiculo', 'placa_vehiculo', 'chasis_vehiculo', 'color_vehiculo', 'uso_vehiculo'],
      'valores': ['valor_asegurado', 'suma_asegurada', 'prima_neta', 'impuestos', 'otros_cargos', 'prima_total', 'deducible', 'deducible_porcentaje'],
      'coberturas': ['cobertura_danos_propios', 'cobertura_robo', 'cobertura_responsabilidad_civil', 'cobertura_lesiones_corporales', 'cobertura_danos_propiedad', 'cobertura_gastos_medicos', 'cobertura_muerte_accidental', 'cobertura_asistencia_vial', 'cobertura_auto_sustituto', 'cobertura_cristales'],
      'condiciones': ['exclusiones', 'condiciones_especiales', 'endosos'],
      'intermediario': ['nombre_intermediario', 'codigo_intermediario', 'comision']
    };
    
    for (const [cat, campos] of Object.entries(categorias)) {
      if (campos.includes(campoId)) return cat;
    }
    return 'otros';
  };

  const getCategoriaLabel = (cat) => {
    const labels = {
      'aseguradora': 'Datos de la Aseguradora',
      'asegurado': 'Datos del Asegurado',
      'poliza': 'Datos de la Póliza',
      'facturacion': 'Facturación',
      'vehiculo': 'Datos del Vehículo',
      'valores': 'Valores y Primas',
      'coberturas': 'Coberturas',
      'limites': 'Límites de Coberturas',
      'deducibles': 'Deducibles por Cobertura',
      'condiciones': 'Condiciones y Exclusiones',
      'intermediario': 'Intermediario',
      'conductor': 'Conductor / Operador',
      'otros': 'Otros Campos'
    };
    return labels[cat] || cat;
  };

  const getCategoriaColor = (cat) => {
    const colors = {
      'aseguradora': 'bg-blue-100 text-blue-800 border-blue-200',
      'asegurado': 'bg-green-100 text-green-800 border-green-200',
      'poliza': 'bg-purple-100 text-purple-800 border-purple-200',
      'facturacion': 'bg-pink-100 text-pink-800 border-pink-200',
      'vehiculo': 'bg-orange-100 text-orange-800 border-orange-200',
      'valores': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'coberturas': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'limites': 'bg-cyan-100 text-cyan-800 border-cyan-200',
      'deducibles': 'bg-amber-100 text-amber-800 border-amber-200',
      'condiciones': 'bg-red-100 text-red-800 border-red-200',
      'intermediario': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'conductor': 'bg-teal-100 text-teal-800 border-teal-200',
      'otros': 'bg-slate-100 text-slate-800 border-slate-200'
    };
    return colors[cat] || colors['otros'];
  };

  const toggleCampo = (campoId) => {
    setCamposActivos(prev => ({
      ...prev,
      [campoId]: !prev[campoId]
    }));
  };

  const toggleCategoria = (categoria) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoria]: !prev[categoria]
    }));
  };

  const guardarPlantilla = async () => {
    if (!analysisResult) return;
    
    const nombre = nombreAseguradora.trim();
    if (!nombre) {
      toast.error('Debe ingresar el nombre de la aseguradora');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Preparar campos con estado activo (de la pestaña campos mapeados)
      const camposConEstado = (analysisResult.campos_encontrados || []).map(campo => ({
        ...campo,
        activo: camposActivos[campo.id] ?? true
      }));

      // Preparar los valores leídos con los mapeos editados por el usuario
      const valoresConMapeo = (analysisResult.todos_los_valores || []).map((valor, idx) => ({
        campo_original: valor.campo_original,
        valor: valor.valor,
        tipo_dato: valor.tipo_dato,
        campo_sistema: valoresEditados[idx] || 'sin_mapear',
        activo: valoresActivos[idx] !== false
      }));

      const formData = new FormData();
      formData.append('nombre_aseguradora', nombre);
      formData.append('tipo_documento', analysisResult.tipo_documento || 'poliza');
      formData.append('campos', JSON.stringify(camposConEstado));
      formData.append('valores_mapeados', JSON.stringify(valoresConMapeo));
      formData.append('patrones_identificacion', JSON.stringify(analysisResult.patrones_identificacion || []));
      formData.append('documento_id', analysisResult.documento_id || '');

      const response = await fetch(`${API_URL}/api/plantillas-aseguradoras/guardar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPlantillas();
        setAnalysisResult(null);
        setSelectedFile(null);
        setCamposActivos({});
        setNombreAseguradora("");
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar la plantilla');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error al guardar la plantilla');
    } finally {
      setSaving(false);
    }
  };

  const togglePlantillaActiva = async (plantillaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/plantillas-aseguradoras/${plantillaId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.activa ? 'Plantilla activada' : 'Plantilla desactivada');
        fetchPlantillas();
      }
    } catch (error) {
      toast.error('Error al cambiar estado de la plantilla');
    }
  };

  const eliminarPlantilla = async (plantillaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta plantilla?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/plantillas-aseguradoras/${plantillaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Plantilla eliminada');
        fetchPlantillas();
      }
    } catch (error) {
      toast.error('Error al eliminar la plantilla');
    }
  };

  const agruparCamposPorCategoria = (campos) => {
    const grupos = {};
    campos.forEach(campo => {
      const cat = campo.categoria || getCategoriaDelCampo(campo.id);
      if (!grupos[cat]) grupos[cat] = [];
      grupos[cat].push(campo);
    });
    return grupos;
  };

  const abrirEdicion = (plantilla) => {
    setEditingPlantilla(plantilla);
    setEditNombreAseg(plantilla.nombre_aseguradora || '');
    
    // Inicializar estados de valores mapeados
    const vActivos = {};
    const vMapeos = {};
    (plantilla.valores_mapeados || []).forEach((v, idx) => {
      vActivos[idx] = v.activo !== false;
      vMapeos[idx] = v.campo_sistema || 'sin_mapear';
    });
    setEditValoresActivos(vActivos);
    setEditValoresMapeados(vMapeos);
    
    // Inicializar estados de campos
    const cActivos = {};
    const cats = {};
    (plantilla.campos || []).forEach(c => {
      cActivos[c.id] = c.activo !== false;
      const cat = c.categoria || getCategoriaDelCampo(c.id);
      cats[cat] = true;
    });
    setEditCamposActivos(cActivos);
    setEditExpandedCats(cats);
    setEditActiveTab('valores');
  };

  const guardarEdicionPlantilla = async () => {
    if (!editingPlantilla) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      
      const camposActualizados = (editingPlantilla.campos || []).map(c => ({
        ...c,
        activo: editCamposActivos[c.id] !== false
      }));
      
      const valoresActualizados = (editingPlantilla.valores_mapeados || []).map((v, idx) => ({
        ...v,
        activo: editValoresActivos[idx] !== false,
        campo_sistema: editValoresMapeados[idx] || v.campo_sistema || 'sin_mapear'
      }));

      const response = await fetch(`${API_URL}/api/plantillas-aseguradoras/${editingPlantilla.id}/campos`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campos: camposActualizados,
          valores_mapeados: valoresActualizados,
          nombre_aseguradora: editNombreAseg.trim() || editingPlantilla.nombre_aseguradora
        })
      });

      if (response.ok) {
        toast.success('Plantilla actualizada correctamente');
        fetchPlantillas();
        setEditingPlantilla(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al actualizar');
      }
    } catch (error) {
      toast.error('Error al actualizar la plantilla');
    } finally {
      setSavingEdit(false);
    }
  };

  const renderCamposAgrupados = () => {
    if (!analysisResult?.campos_encontrados?.length) return null;
    
    const grupos = agruparCamposPorCategoria(analysisResult.campos_encontrados);
    
    return Object.entries(grupos).map(([categoria, campos]) => (
      <Collapsible 
        key={categoria} 
        open={expandedCategories[categoria]}
        onOpenChange={() => toggleCategoria(categoria)}
        className="mb-3"
      >
        <CollapsibleTrigger asChild>
          <div className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-slate-50 border ${getCategoriaColor(categoria)}`}>
            <div className="flex items-center gap-2">
              {expandedCategories[categoria] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span className="font-medium">{getCategoriaLabel(categoria)}</span>
              <Badge variant="secondary" className="ml-2">{campos.length} campos</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  campos.forEach(c => setCamposActivos(prev => ({ ...prev, [c.id]: true })));
                }}
              >
                Activar todos
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  campos.forEach(c => setCamposActivos(prev => ({ ...prev, [c.id]: false })));
                }}
              >
                Desactivar todos
              </Button>
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="pl-6 pt-2 space-y-2">
            {campos.map(campo => (
              <div 
                key={campo.id}
                className={`p-3 rounded-lg border ${camposActivos[campo.id] ? 'bg-white border-green-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={camposActivos[campo.id] ?? true}
                        onCheckedChange={() => toggleCampo(campo.id)}
                      />
                      <span className="font-medium text-sm">{campo.nombre}</span>
                      {campo.confianza && (
                        <Badge variant={campo.confianza === 'alta' ? 'default' : campo.confianza === 'media' ? 'secondary' : 'outline'}>
                          {campo.confianza}
                        </Badge>
                      )}
                    </div>
                    {campo.valor_ejemplo && (
                      <div className="mt-1 text-sm text-slate-600 pl-8">
                        <span className="font-medium">Valor:</span> {campo.valor_ejemplo}
                      </div>
                    )}
                    {campo.ubicacion && (
                      <div className="mt-1 text-xs text-slate-500 pl-8 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {campo.ubicacion}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    ));
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <div className="ml-0 md:ml-64 min-h-screen">
        <div className="p-6 space-y-6" data-testid="plantillas-aseguradoras-page">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Building2 className="w-7 h-7 text-blue-600" />
                Plantillas de Aseguradoras
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Configure plantillas para mejorar la precisión en las comparaciones de pólizas
              </p>
            </div>
            <Button variant="outline" onClick={fetchPlantillas}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>

          <Tabs defaultValue="nueva" className="space-y-4">
            <TabsList>
              <TabsTrigger value="nueva">
                <Upload className="w-4 h-4 mr-2" />
                Nueva Plantilla
              </TabsTrigger>
              <TabsTrigger value="guardadas">
                <FileText className="w-4 h-4 mr-2" />
            Plantillas Guardadas ({plantillas.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="nueva" className="space-y-4">
          {/* Zona de carga de documento */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cargar Documento de Ejemplo</CardTitle>
              <CardDescription>
                Suba un documento de póliza de la aseguradora. El sistema analizará su estructura y extraerá los campos.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                {selectedFile ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button 
                      onClick={(e) => { e.stopPropagation(); analyzeDocument(); }}
                      disabled={analyzing}
                    >
                      {analyzing ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Analizando...
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Analizar Documento
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-slate-600">
                      {isDragActive
                        ? 'Suelte el archivo aquí...'
                        : 'Arrastre un archivo aquí o haga clic para seleccionar'}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      Formatos soportados: PDF, DOC, DOCX
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Resultados del análisis */}
          {analysisResult && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      Análisis Completado
                    </CardTitle>
                    <div className="mt-3">
                      <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                        Nombre de la Aseguradora <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={nombreAseguradora}
                        onChange={(e) => setNombreAseguradora(e.target.value)}
                        placeholder="Ej: Seguros Universal, MAPFRE BHD, etc."
                        className={`w-full max-w-md px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          nombreAseguradora.trim() 
                            ? 'border-blue-300 bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:border-blue-600 dark:text-blue-300' 
                            : 'border-red-300 bg-red-50 text-slate-800 dark:bg-red-900/20 dark:border-red-600 dark:text-slate-300'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        data-testid="nombre-aseguradora-input"
                      />
                      {!nombreAseguradora.trim() && (
                        <p className="text-xs text-red-500 mt-1">Ingrese el nombre de la aseguradora para poder guardar la plantilla</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4 self-start">
                    <Button variant="outline" onClick={() => setShowPreview(true)}>
                      <Eye className="w-4 h-4 mr-2" />
                      Ver Texto Extraído
                    </Button>
                    <Button onClick={guardarPlantilla} disabled={saving || !nombreAseguradora.trim()}>
                      {saving ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Guardar Plantilla
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Tipo de documento:</span>
                      <p className="font-medium">{analysisResult.tipo_documento}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Total valores leídos:</span>
                      <p className="font-medium text-purple-600">{analysisResult.total_valores_leidos || 0}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Campos mapeados:</span>
                      <p className="font-medium text-green-600">{analysisResult.total_campos_detectados}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Campos activos:</span>
                      <p className="font-medium text-blue-600">
                        {Object.values(camposActivos).filter(Boolean).length}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500">No encontrados:</span>
                      <p className="font-medium text-orange-600">
                        {analysisResult.campos_no_encontrados?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tabs de resultados */}
                <Tabs value={activeResultTab} onValueChange={setActiveResultTab} className="space-y-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="todos_valores" className="text-sm">
                      Todos los Valores Leídos ({analysisResult.todos_los_valores?.length || 0})
                    </TabsTrigger>
                    <TabsTrigger value="campos_mapeados" className="text-sm">
                      Campos Mapeados ({analysisResult.total_campos_detectados})
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Tab: Todos los valores leídos del documento */}
                  <TabsContent value="todos_valores">
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium">Estos son TODOS los valores que la IA leyó del documento.</p>
                          <p>Revise esta lista para verificar que los valores están correctamente identificados. Si un campo está mal asignado (ej: un monto que no es "valor asegurado"), puede corregirlo en la columna "Campo Sistema".</p>
                        </div>
                      </div>
                    </div>
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-2">
                        {(analysisResult.todos_los_valores || []).map((valor, idx) => (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg border transition-colors ${
                              valoresActivos[idx] === false 
                                ? 'bg-slate-100 opacity-60 border-slate-200' 
                                : 'bg-white hover:bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="grid grid-cols-12 gap-3 items-center">
                              {/* Switch activar/desactivar */}
                              <div className="col-span-1 flex justify-center">
                                <Switch
                                  checked={valoresActivos[idx] !== false}
                                  onCheckedChange={(checked) => {
                                    setValoresActivos(prev => ({ ...prev, [idx]: checked }));
                                  }}
                                />
                              </div>
                              {/* Campo original */}
                              <div className="col-span-3">
                                <p className="text-xs text-slate-500">Campo en Documento</p>
                                <p className="text-sm font-semibold text-slate-800">{valor.campo_original}</p>
                              </div>
                              {/* Valor leído */}
                              <div className="col-span-3">
                                <p className="text-xs text-slate-500">Valor Leído</p>
                                <p className="text-sm font-bold text-blue-700">{valor.valor}</p>
                              </div>
                              {/* Tipo */}
                              <div className="col-span-1">
                                <p className="text-xs text-slate-500">Tipo</p>
                                <Badge variant="outline" className="text-xs">
                                  {valor.tipo_dato || 'texto'}
                                </Badge>
                              </div>
                              {/* Dropdown mapeo a campo del sistema */}
                              <div className="col-span-4">
                                <p className="text-xs text-slate-500 mb-1">Mapear a Campo del Sistema</p>
                                <Select
                                  value={valoresEditados[idx] || 'sin_mapear'}
                                  onValueChange={(newValue) => {
                                    setValoresEditados(prev => ({ ...prev, [idx]: newValue }));
                                  }}
                                  disabled={valoresActivos[idx] === false}
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="sin_mapear">
                                      Sin mapear
                                    </SelectItem>
                                    {camposSistema.map(campo => (
                                      <SelectItem key={campo.id} value={campo.id}>
                                        {campo.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        ))}
                        {(!analysisResult.todos_los_valores || analysisResult.todos_los_valores.length === 0) && (
                          <div className="text-center py-8 text-slate-500">
                            No se encontraron valores adicionales
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  
                  {/* Tab: Campos mapeados al sistema */}
                  <TabsContent value="campos_mapeados">
                    <div className="mb-4">
                      <h4 className="font-medium text-sm text-slate-700 mb-2">
                        Seleccione los campos que desea incluir en las comparaciones:
                      </h4>
                    </div>
                    <ScrollArea className="h-[500px] pr-4">
                      {renderCamposAgrupados()}
                    </ScrollArea>
                  </TabsContent>
                </Tabs>

                {analysisResult.observaciones && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800">Observaciones</p>
                        <p className="text-sm text-yellow-700">{analysisResult.observaciones}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="guardadas">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Plantillas Guardadas</CardTitle>
              <CardDescription>
                Gestione las plantillas de aseguradoras configuradas en el sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : plantillas.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No hay plantillas configuradas</p>
                  <p className="text-sm">Cargue un documento en la pestaña "Nueva Plantilla" para comenzar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {plantillas.map(plantilla => (
                    <div 
                      key={plantilla.id}
                      className={`p-4 rounded-lg border ${plantilla.activa ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Building2 className={`w-8 h-8 ${plantilla.activa ? 'text-blue-600' : 'text-slate-400'}`} />
                          <div>
                            <h4 className="font-semibold text-slate-900">{plantilla.nombre_aseguradora}</h4>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Badge variant="outline">{plantilla.tipo_documento}</Badge>
                              <span>{plantilla.campos?.length || 0} campos</span>
                              <span>({plantilla.campos?.filter(c => c.activo !== false).length || 0} activos)</span>
                              {plantilla.valores_mapeados?.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span>{plantilla.valores_mapeados.length} valores</span>
                                  <span>({plantilla.valores_mapeados.filter(v => v.activo !== false).length} activos)</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => abrirEdicion(plantilla)}
                            data-testid={`edit-plantilla-${plantilla.id}`}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePlantillaActiva(plantilla.id)}
                          >
                            {plantilla.activa ? (
                              <ToggleRight className="w-5 h-5 text-green-600" />
                            ) : (
                              <ToggleLeft className="w-5 h-5 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => eliminarPlantilla(plantilla.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog de Vista Previa del Texto Extraído */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Texto Completo Extraído del Documento</DialogTitle>
            <DialogDescription>
              Este es el texto que la IA lee del documento. Use esto para verificar si los campos se están leyendo correctamente.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[600px]">
            <pre className="text-xs bg-slate-50 p-4 rounded-lg whitespace-pre-wrap font-mono leading-5">
              {analysisResult?.texto_extraido_preview}
            </pre>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edición de Plantilla - Completo */}
      <Dialog open={!!editingPlantilla} onOpenChange={() => setEditingPlantilla(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Editar Plantilla
            </DialogTitle>
            <DialogDescription>
              Active o desactive campos y valores para esta aseguradora
            </DialogDescription>
          </DialogHeader>
          
          {/* Nombre editable */}
          <div className="mb-2">
            <Label className="text-sm font-medium">Nombre de la Aseguradora</Label>
            <Input
              value={editNombreAseg}
              onChange={(e) => setEditNombreAseg(e.target.value)}
              className="mt-1 max-w-md"
              data-testid="edit-nombre-aseguradora"
            />
          </div>

          {/* Resumen */}
          {editingPlantilla && (
            <div className="grid grid-cols-4 gap-3 mb-2 p-3 bg-slate-50 rounded-lg text-sm">
              <div>
                <span className="text-slate-500">Tipo:</span>
                <p className="font-medium">{editingPlantilla.tipo_documento || 'poliza'}</p>
              </div>
              <div>
                <span className="text-slate-500">Valores leídos:</span>
                <p className="font-medium text-purple-600">{editingPlantilla.valores_mapeados?.length || 0}</p>
              </div>
              <div>
                <span className="text-slate-500">Campos mapeados:</span>
                <p className="font-medium text-green-600">{editingPlantilla.campos?.length || 0}</p>
              </div>
              <div>
                <span className="text-slate-500">Activos:</span>
                <p className="font-medium text-blue-600">
                  {Object.values(editCamposActivos).filter(Boolean).length} campos / {Object.values(editValoresActivos).filter(Boolean).length} valores
                </p>
              </div>
            </div>
          )}

          {/* Tabs de edición */}
          <Tabs value={editActiveTab} onValueChange={setEditActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="valores" className="text-sm" data-testid="edit-tab-valores">
                Valores Leídos ({editingPlantilla?.valores_mapeados?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="campos" className="text-sm" data-testid="edit-tab-campos">
                Campos Mapeados ({editingPlantilla?.campos?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* Tab: Valores leídos */}
            <TabsContent value="valores">
              <ScrollArea className="h-[400px]">
                {editingPlantilla?.valores_mapeados?.length > 0 ? (
                  <div className="space-y-2 pr-3">
                    <div className="flex justify-end gap-2 mb-2">
                      <Button variant="outline" size="sm" onClick={() => {
                        const all = {};
                        editingPlantilla.valores_mapeados.forEach((_, i) => { all[i] = true; });
                        setEditValoresActivos(all);
                      }} data-testid="edit-activar-todos-valores">
                        Activar todos
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => {
                        const all = {};
                        editingPlantilla.valores_mapeados.forEach((_, i) => { all[i] = false; });
                        setEditValoresActivos(all);
                      }} data-testid="edit-desactivar-todos-valores">
                        Desactivar todos
                      </Button>
                    </div>
                    {editingPlantilla.valores_mapeados.map((valor, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border transition-colors ${
                          editValoresActivos[idx] === false
                            ? 'bg-slate-100 border-slate-200 opacity-60'
                            : 'bg-white border-slate-200 hover:border-blue-200'
                        }`}
                        data-testid={`edit-valor-row-${idx}`}
                      >
                        <div className="grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-1 flex justify-center">
                            <Switch
                              checked={editValoresActivos[idx] !== false}
                              onCheckedChange={(checked) => {
                                setEditValoresActivos(prev => ({ ...prev, [idx]: checked }));
                              }}
                              data-testid={`edit-valor-switch-${idx}`}
                            />
                          </div>
                          <div className="col-span-3">
                            <p className="text-xs text-slate-500">Campo en Documento</p>
                            <p className="text-sm font-semibold text-slate-800">{valor.campo_original}</p>
                          </div>
                          <div className="col-span-3">
                            <p className="text-xs text-slate-500">Valor Leído</p>
                            <p className="text-sm font-bold text-blue-700">{valor.valor}</p>
                          </div>
                          <div className="col-span-1">
                            <p className="text-xs text-slate-500">Tipo</p>
                            <Badge variant="outline" className="text-xs">
                              {valor.tipo_dato || 'texto'}
                            </Badge>
                          </div>
                          <div className="col-span-4">
                            <p className="text-xs text-slate-500 mb-1">Campo del Sistema</p>
                            <Select
                              value={editValoresMapeados[idx] || 'sin_mapear'}
                              onValueChange={(newValue) => {
                                setEditValoresMapeados(prev => ({ ...prev, [idx]: newValue }));
                              }}
                              disabled={editValoresActivos[idx] === false}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sin_mapear">Sin mapear</SelectItem>
                                {camposSistema.map(campo => (
                                  <SelectItem key={campo.id} value={campo.id}>
                                    {campo.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p>Esta plantilla no tiene valores leídos guardados</p>
                    <p className="text-xs mt-1">Puede re-analizar un documento para obtener valores actualizados</p>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Tab: Campos mapeados al sistema */}
            <TabsContent value="campos">
              <ScrollArea className="h-[400px]">
                {editingPlantilla?.campos?.length > 0 ? (
                  <div className="pr-3">
                    {(() => {
                      const grupos = agruparCamposPorCategoria(editingPlantilla.campos);
                      return Object.entries(grupos).map(([categoria, campos]) => (
                        <Collapsible
                          key={categoria}
                          open={editExpandedCats[categoria]}
                          onOpenChange={() => setEditExpandedCats(prev => ({ ...prev, [categoria]: !prev[categoria] }))}
                          className="mb-3"
                        >
                          <CollapsibleTrigger asChild>
                            <div className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-slate-50 border ${getCategoriaColor(categoria)}`}>
                              <div className="flex items-center gap-2">
                                {editExpandedCats[categoria] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                <span className="font-medium">{getCategoriaLabel(categoria)}</span>
                                <Badge variant="secondary" className="ml-2">{campos.length}</Badge>
                                <Badge variant="outline" className="ml-1 text-xs">
                                  {campos.filter(c => editCamposActivos[c.id] !== false).length} activos
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={(e) => {
                                  e.stopPropagation();
                                  campos.forEach(c => setEditCamposActivos(prev => ({ ...prev, [c.id]: true })));
                                }}>
                                  Activar
                                </Button>
                                <Button variant="ghost" size="sm" onClick={(e) => {
                                  e.stopPropagation();
                                  campos.forEach(c => setEditCamposActivos(prev => ({ ...prev, [c.id]: false })));
                                }}>
                                  Desactivar
                                </Button>
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="pl-6 pt-2 space-y-2">
                              {campos.map(campo => (
                                <div
                                  key={campo.id}
                                  className={`p-3 rounded-lg border ${editCamposActivos[campo.id] !== false ? 'bg-white border-green-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}
                                  data-testid={`edit-campo-${campo.id}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={editCamposActivos[campo.id] !== false}
                                        onCheckedChange={(checked) => {
                                          setEditCamposActivos(prev => ({ ...prev, [campo.id]: checked }));
                                        }}
                                        data-testid={`edit-campo-switch-${campo.id}`}
                                      />
                                      <span className="font-medium text-sm">{campo.nombre}</span>
                                      {campo.confianza && (
                                        <Badge variant={campo.confianza === 'alta' ? 'default' : 'secondary'}>
                                          {campo.confianza}
                                        </Badge>
                                      )}
                                    </div>
                                    {campo.valor_ejemplo && (
                                      <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                                        {campo.valor_ejemplo}
                                      </span>
                                    )}
                                  </div>
                                  {campo.ubicacion && (
                                    <div className="mt-1 text-xs text-slate-500 pl-8 flex items-center gap-1">
                                      <MapPin className="w-3 h-3" />
                                      {campo.ubicacion}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ));
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p>Esta plantilla no tiene campos mapeados</p>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditingPlantilla(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={guardarEdicionPlantilla} 
              disabled={savingEdit}
              data-testid="edit-guardar-btn"
            >
              {savingEdit ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
      </div>
    </div>
  );
};

export default PlantillasAseguradorasPage;
